import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../api/axiosConfig';
import { apiErrorText } from '../utils/apiErrors';
import ExportPreviewModal from '../components/ExportPreviewModal';
import HistorySummary from '../components/HistorySummary';
import RecobroChart from '../components/RecobroChart';
import './CompaniesPage.css';
import './SalesFormPage.css';
import '../styles/topbarButtons.css';
import '../styles/recobro.css';
import '../styles/pageHeader.css';

// Recobro de cintas (Producción), laid out like the farms' RECOBRO sheet: one row per enfunde
// week, one column per harvest week of the year, so
// the harvested counts step down diagonally. Data: GET recobro/?farm=&year=&company= (core/recobro.py).

const FARM_KEY = 'farmershub_recobro_farm';
const COMPANY_KEY = 'farmershub_recobro_company';
const ALL_FARMS = 'all'; // "Todas las fincas": every farm added together (see build_recobro_all)

// Same colors as the sheet: harvested total in yellow, saldo in light blue.
const TOTAL_FILL = 'FFFF00';
const SALDO_FILL = 'C6D9F0';

const int = (v) => (v == null || v === '' ? '' : Number(v).toLocaleString('es-EC'));
const dec = (v) => (v == null ? '' : v.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const hex6 = (hex) => (hex ? hex.replace('#', '').slice(0, 6).toUpperCase() : null);

// Text on a solid ribbon color: white on dark ribbons (NEGRA, CAFE, AZUL…), black otherwise.
function inkFor(hex) {
  const h = hex6(hex);
  if (!h) return '000000';
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.substring(i, i + 2), 16));
  return (0.299 * r + 0.587 * g + 0.114 * b) < 140 ? 'FFFFFF' : '000000';
}

function sheetModel(data) {
  const cols = data.columns;
  const weekCell = (r) => `${r.enfunde_week}${r.enfunde_year !== data.year ? `/${String(r.enfunde_year).slice(2)}` : ''}`;
  return {
    cols,
    title: data.farm.id == null
      ? `${data.farm.name.toUpperCase()} ${data.year}`
      : `HACIENDA ${data.farm.name.toUpperCase()} ${data.year}`,
    head: ['SEM.', 'CINTA', 'ENFUNDE', ...cols.map((c) => String(c.week)), 'RAC. COSEC.', 'SALDO', '% RECOB.', '% SALDO'],
    rows: data.rows.map((r) => ({
      row: r,
      cells: [
        weekCell(r), r.ribbon_color || '—', r.enfunde ?? '',
        ...cols.map((c) => r.cells[c.id] ?? ''),
        r.harvested, r.saldo ?? '', r.recovery ?? '', r.saldo_ratio ?? '',
      ],
    })),
    cut: ['RACIMOS CORTADOS', '', '', ...cols.map((c) => data.cut[c.id] || ''), data.totals.harvested, '', '', ''],
    ratio: ['RATIO', '', '', ...cols.map((c) => data.ratio[c.id] ?? ''), data.totals.ratio ?? '', '', '', ''],
  };
}

function RecobroSheet({ data }) {
  const { cols, title } = sheetModel(data);
  const scrollRef = useRef(null);

  // Open scrolled to the first harvest week with data, so the "stairs" are in view
  // (every week of the year is still there to scroll through, like the sheet).
  useEffect(() => {
    const box = scrollRef.current;
    const first = data.columns.findIndex((c) => data.cut[c.id]);
    if (!box || first < 0) return;
    const ths = box.querySelectorAll('thead tr:last-child th');
    const th = ths[3 + first];
    // Width of the frozen SEM./CINTA/ENFUNDE columns (sticky cells report offsets against the
    // page, so add up their widths rather than using their offsetLeft).
    const frozen = [0, 1, 2].reduce((w, i) => w + (ths[i]?.offsetWidth || 0), 0);
    if (th) box.scrollLeft = Math.max(0, th.offsetLeft - frozen - 60);
  }, [data]); // only when new data loads, so it never fights the user's own scrolling

  return (
    <div className="recobro-sheet-wrap">
      <div className="recobro-sheet-title">{title}</div>
      <div className="recobro-sheet-subtitle">RECOBRO</div>
      <div className="recobro-scroll" ref={scrollRef}>
        <table className="recobro-sheet">
          <thead>
            <tr>
              <th className="recobro-sticky recobro-col-week recobro-blank" />
              <th className="recobro-sticky recobro-col-cinta recobro-year-head">{data.year}</th>
              <th className="recobro-sticky recobro-col-enfunde recobro-blank" />
              <th colSpan={cols.length + 4} className="recobro-blank" />
            </tr>
            <tr>
              <th className="recobro-sticky recobro-col-week">SEM.</th>
              <th className="recobro-sticky recobro-col-cinta">CINTA</th>
              <th className="recobro-sticky recobro-col-enfunde">ENFUNDE</th>
              {cols.map((c) => (
                <th key={c.id} className={c.year !== data.year ? 'recobro-prev-year' : undefined} title={`Semana ${c.week} de ${c.year}`}>
                  {c.week}
                </th>
              ))}
              <th className="recobro-total-col">RAC. COSEC.</th>
              <th className="recobro-saldo-col">SALDO</th>
              <th>% RECOB.</th>
              <th>% SALDO</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => {
              const fill = hex6(r.ribbon_hex_color);
              return (
                <tr key={r.key}>
                  <td className="recobro-sticky recobro-col-week">
                    {r.enfunde_week}
                    {r.enfunde_year !== data.year && <span className="recobro-year">/{String(r.enfunde_year).slice(2)}</span>}
                  </td>
                  <td
                    className="recobro-sticky recobro-col-cinta recobro-cinta"
                    style={fill ? { background: `#${fill}`, color: `#${inkFor(fill)}` } : undefined}
                  >
                    {r.ribbon_color || '—'}
                  </td>
                  <td
                    className="recobro-sticky recobro-col-enfunde recobro-enfunde"
                    title={r.enfunde == null ? 'No hay hoja de Control de Enfunde para esta semana y cinta' : undefined}
                  >
                    {int(r.enfunde)}
                  </td>
                  {cols.map((c) => <td key={c.id} className="recobro-cell">{int(r.cells[c.id])}</td>)}
                  <td className="recobro-total-col recobro-total-value">{int(r.harvested)}</td>
                  <td className="recobro-saldo-col">{int(r.saldo)}</td>
                  <td>{dec(r.recovery)}</td>
                  <td>{dec(r.saldo_ratio)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="recobro-foot">
              <td className="recobro-sticky recobro-foot-label" colSpan={3}>RACIMOS CORTADOS</td>
              {cols.map((c) => <td key={c.id}>{int(data.cut[c.id] || null)}</td>)}
              <td className="recobro-total-col">{int(data.totals.harvested)}</td>
              <td colSpan={3} />
            </tr>
            <tr className="recobro-foot">
              <td className="recobro-sticky recobro-foot-label" colSpan={3}>RATIO</td>
              {cols.map((c) => <td key={c.id} className="recobro-ratio">{dec(data.ratio[c.id])}</td>)}
              <td className="recobro-ratio" title="Total cajas (43lbs) ÷ racimos procesados del año">{dec(data.totals.ratio)}</td>
              <td colSpan={3} />
            </tr>
            <tr>
              <td className="recobro-sticky recobro-foot-label recobro-enfunde-total" colSpan={3} title="Total de racimos enfundados de las filas">
                {int(data.totals.enfunde)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

async function exportExcel(data) {
  const { title, head, rows, cut, ratio, cols } = sheetModel(data);
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(`RECOBRO ${data.year}`, { views: [{ state: 'frozen', xSplit: 3, ySplit: 5 }] });
  const lastCol = head.length;
  const thin = { style: 'thin', color: { argb: 'FF000000' } };
  const border = { top: thin, left: thin, bottom: thin, right: thin };
  const solid = (hex) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${hex}` } });

  sheet.mergeCells(1, 1, 1, lastCol);
  sheet.getCell(1, 1).value = title;
  sheet.getCell(1, 1).font = { name: 'Rockwell', size: 22, bold: true };
  sheet.mergeCells(2, 1, 2, lastCol);
  sheet.getCell(2, 1).value = 'RECOBRO';
  sheet.getCell(2, 1).font = { name: 'Rockwell', size: 20, bold: true, color: { argb: 'FFFF0000' } };
  [1, 2].forEach((r) => { sheet.getCell(r, 1).alignment = { horizontal: 'center' }; sheet.getRow(r).height = 28; });
  sheet.addRow([]);
  const yearRow = sheet.addRow(['', data.year]);
  yearRow.getCell(2).font = { name: 'Arial', size: 12, bold: true };
  yearRow.getCell(2).alignment = { horizontal: 'center' };

  const headRow = sheet.addRow(head);
  headRow.eachCell((c) => { c.font = { name: 'Arial', size: 8 }; c.alignment = { horizontal: 'center', wrapText: true }; c.border = border; });
  const totalIdx = 3 + cols.length + 1; // 1-based column of RAC. COSEC.

  rows.forEach(({ row, cells }) => {
    const xr = sheet.addRow(cells);
    xr.eachCell({ includeEmpty: true }, (c, i) => {
      c.border = border;
      c.font = { name: 'Arial', size: 8 };
      c.alignment = { horizontal: i === 2 ? 'left' : 'center' };
    });
    const fill = hex6(row.ribbon_hex_color);
    if (fill) {
      xr.getCell(2).fill = solid(fill);
      xr.getCell(2).font = { name: 'Arial', size: 9, bold: true, color: { argb: `FF${inkFor(fill)}` } };
    }
    xr.getCell(totalIdx).fill = solid(TOTAL_FILL);
    xr.getCell(totalIdx).font = { name: 'Rockwell', size: 12 };
    xr.getCell(totalIdx + 1).fill = solid(SALDO_FILL);
    xr.getCell(totalIdx + 2).numFmt = '0.00';
    xr.getCell(totalIdx + 3).numFmt = '0.00';
  });
  sheet.addRow([]);
  [cut, ratio].forEach((cells, k) => {
    const xr = sheet.addRow(cells);
    sheet.mergeCells(xr.number, 1, xr.number, 3);
    xr.eachCell({ includeEmpty: true }, (c, i) => {
      if (i > totalIdx) return;
      c.border = border;
      c.font = { name: 'Arial', size: 10, bold: true };
      c.alignment = { horizontal: 'center' };
      if (k === 1 && i > 3) c.numFmt = '0.00';
    });
    sheet.addRow([]);
  });
  const enfundeRow = sheet.addRow([data.totals.enfunde]);
  sheet.mergeCells(enfundeRow.number, 1, enfundeRow.number, 3);
  enfundeRow.getCell(1).fill = solid(TOTAL_FILL);
  enfundeRow.getCell(1).font = { name: 'Arial', size: 11, bold: true };
  enfundeRow.getCell(1).alignment = { horizontal: 'center' };
  enfundeRow.getCell(1).border = border;

  sheet.columns.forEach((col, i) => { col.width = i === 0 ? 5 : i === 1 ? 14 : i === 2 ? 9 : i >= totalIdx - 1 ? 10 : 5.5; });
  const buffer = await workbook.xlsx.writeBuffer();
  const url = URL.createObjectURL(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `Recobro_${data.farm.name.replace(/\s+/g, '_')}_${data.year}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function exportPDF(data) {
  const { title, head, rows, cut, ratio, cols } = sheetModel(data);
  const doc = new jsPDF({ orientation: 'landscape', format: 'a3' });
  const pageW = doc.internal.pageSize.getWidth();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(title, pageW / 2, 14, { align: 'center' });
  doc.setTextColor(255, 0, 0);
  doc.setFontSize(15);
  doc.text('RECOBRO', pageW / 2, 21, { align: 'center' });
  doc.setTextColor(0, 0, 0);
  const totalIdx = 3 + cols.length; // 0-based column of RAC. COSEC.
  const fmt = (v, i) => {
    if (v === '' || v == null) return '';
    if (i >= totalIdx + 2 && typeof v === 'number') return dec(v);
    return typeof v === 'number' ? int(v) : v;
  };
  autoTable(doc, {
    startY: 26,
    theme: 'grid',
    head: [head],
    body: [
      ...rows.map(({ cells }) => cells.map(fmt)),
      cut.map(fmt),
      ratio.map((v, i) => (i > 2 && v !== '' ? dec(v) : v)),
    ],
    styles: { fontSize: 5.5, cellPadding: 0.6, halign: 'center', lineColor: [0, 0, 0], lineWidth: 0.1, textColor: [0, 0, 0] },
    headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'normal' },
    didParseCell: (cell) => {
      if (cell.section !== 'body') return;
      const i = cell.column.index;
      const entry = rows[cell.row.index];
      if (!entry) { cell.cell.styles.fontStyle = 'bold'; return; }
      const fill = hex6(entry.row.ribbon_hex_color);
      if (i === 1 && fill) {
        cell.cell.styles.fillColor = `#${fill}`;
        cell.cell.styles.textColor = `#${inkFor(fill)}`;
        cell.cell.styles.fontStyle = 'bold';
      }
      if (i === totalIdx) cell.cell.styles.fillColor = `#${TOTAL_FILL}`;
      if (i === totalIdx + 1) cell.cell.styles.fillColor = `#${SALDO_FILL}`;
    },
  });
  doc.save(`Recobro_${data.farm.name.replace(/\s+/g, '_')}_${data.year}.pdf`);
}

export default function RecobroPage() {
  const [allFarms, setAllFarms] = useState([]);
  const [companyId, setCompanyId] = useState(() => {
    try { return localStorage.getItem(COMPANY_KEY) || ''; } catch { return ''; }
  }); // '' = Todas las empresas
  const [farmId, setFarmId] = useState(() => {
    try { return localStorage.getItem(FARM_KEY) || ''; } catch { return ''; }
  });
  const [year, setYear] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(false);

  // Companies come from the farms the user can see (each farm carries its company).
  const companies = useMemo(() => {
    const byId = new Map(allFarms.map((f) => [String(f.company), f.company_name]));
    return [...byId].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [allFarms]);
  const farms = useMemo(
    () => (companyId ? allFarms.filter((f) => String(f.company) === companyId) : allFarms),
    [allFarms, companyId],
  );

  useEffect(() => {
    api.get('farms/').then((r) => setAllFarms(r.data))
      .catch(() => setStatus('No se pudieron cargar las fincas.'));
  }, []);

  // Keep the company / farm valid for what the user can see: a farm of another company falls
  // back to "Todas las fincas" (or the only farm) of the chosen company.
  useEffect(() => {
    if (!allFarms.length) return;
    if (companyId && !companies.some((c) => c.id === companyId)) {
      setCompanyId('');
      return;
    }
    setFarmId((current) => {
      if (farms.some((f) => String(f.id) === current)) return current;
      if (farms.length > 1) return ALL_FARMS;
      return String(farms[0]?.id || '');
    });
  }, [allFarms, companies, companyId, farms]);

  useEffect(() => {
    if (!farmId || !allFarms.length) return;
    if (farmId === ALL_FARMS && farms.length < 2) return; // waiting for the farm to be corrected
    if (farmId !== ALL_FARMS && !farms.some((f) => String(f.id) === farmId)) return;
    try {
      localStorage.setItem(FARM_KEY, farmId);
      localStorage.setItem(COMPANY_KEY, companyId);
    } catch { /* ignore */ }
    setLoading(true);
    setStatus(null);
    const params = { farm: farmId, ...(year ? { year } : {}), ...(farmId === ALL_FARMS && companyId ? { company: companyId } : {}) };
    api.get('recobro/', { params })
      .then((r) => { setData(r.data); if (!year) setYear(String(r.data.year)); })
      .catch((err) => { setData(null); setStatus(apiErrorText(err, 'No se pudo cargar el recobro.')); })
      .finally(() => setLoading(false));
  }, [farmId, year, companyId, farms, allFarms.length]);

  const summary = useMemo(() => (data ? [
    ['Racimos enfundados', data.totals.enfunde],
    ['Racimos cosechados', data.totals.harvested],
    ['Semanas de enfunde', data.rows.filter((r) => r.enfunde).length],
  ] : []), [data]);

  return (
    <div className="companies-root">
      <div className="page-topbar">
        <Link to="/production" className="topbar-btn">← Producción</Link>
        <button type="button" className="topbar-btn topbar-btn-primary" onClick={() => setPreview(true)} disabled={!data || data.rows.length === 0}>
          📤 Exportar
        </button>
      </div>
      <h1 className="companies-title">📈 Recobro de Cintas</h1>
      <p className="recobro-subtitle page-header-divided">
        Racimos enfundados por semana (Control de Enfunde) y cuántos se cosecharon, y en qué semana (Detalles de Cosecha).
      </p>

      <div className="history-filters">
        {companies.length > 1 && (
          <select className="history-select" value={companyId} onChange={(e) => setCompanyId(e.target.value)} aria-label="Empresa">
            <option value="">Todas las empresas</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <select className="history-select" value={farmId} onChange={(e) => setFarmId(e.target.value)} aria-label="Finca">
          {farms.length > 1 && <option value={ALL_FARMS}>Todas las fincas</option>}
          {farms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        <select className="history-select" value={year} onChange={(e) => setYear(e.target.value)} aria-label="Año">
          {(data?.years || (year ? [Number(year)] : [])).map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {status && <div className="companies-status companies-status-error">{status}</div>}

      {loading && !data ? (
        <div className="companies-card"><div className="companies-empty">Cargando…</div></div>
      ) : data && (
        <>
          <HistorySummary cards={summary} />
          <div className="companies-card recobro-card">
            {data.rows.length === 0 ? (
              <div className="companies-empty">No hay enfunde ni cosecha registrados para {data.farm.name} en {data.year}.</div>
            ) : (
              <RecobroSheet data={data} />
            )}
          </div>
          <div className="companies-card recobro-card">
            <div className="recobro-card-title">% de recobro por semana de enfunde</div>
            <RecobroChart rows={data.rows} />
          </div>
        </>
      )}

      {preview && data && (
        <ExportPreviewModal
          title={`${data.farm.name} · Recobro ${data.year}`}
          fileName={`Recobro_${data.farm.name.replace(/\s+/g, '_')}_${data.year}`}
          onClose={() => setPreview(false)}
          onDownload={(fmt) => (fmt === 'excel' ? exportExcel(data) : exportPDF(data))}
        >
          <RecobroSheet data={data} />
        </ExportPreviewModal>
      )}
    </div>
  );
}
